package uz.mint.frontend.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import kotlinx.browser.document
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.seo.IndexablePage
import uz.mint.frontend.seo.applySeo

/**
 * @param description meta description for this page; null uses the locale's site-wide one. The
 *   catalogue page sets its own — every indexable URL wants a description that describes it.
 * @param page which cross-locale page this is, for canonical and hreflang. Null means the page is
 *   not marketing surface and is served `noindex` — see [applySeo]. Defaulting to null is
 *   deliberate: a new signed-in page that forgets to say anything stays out of the index, which
 *   is the safe direction to fail in.
 */
class PageLayoutData(
    val title: String,
    val locale: Locale = Locale.DEFAULT,
    val description: String? = null,
    val page: IndexablePage? = null,
)

/**
 * Page shell.
 *
 * Deliberately thin: the design is full-bleed — every section owns its own background band and
 * draws its own inner max-width container — so a layout-level content column would fight it.
 * Nav and footer live in the page rather than here because the footer opens the legal overlay,
 * whose state belongs next to the sections that read it.
 */
@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    val strings = stringsFor(data.locale)

    // Static export renders the page in a headless browser and snapshots the resulting DOM,
    // so these writes end up in the exported HTML that crawlers actually read.
    LaunchedEffect(data.title, data.locale, data.description, data.page) {
        document.title = data.title
        document.documentElement?.setAttribute("lang", data.locale.htmlLang)
        document.querySelector("meta[name=description]")
            ?.setAttribute("content", data.description ?: strings.metaDescription)
        applySeo(data.page, data.locale)
    }

    Div(
        Modifier
            .fillMaxWidth()
            .maxWidth(100.percent)
            // The hero's mint panel and the oversized display type can overhang on narrow
            // viewports; clipping here stops that becoming a horizontal scrollbar.
            .overflow { x(Overflow.Hidden) }
            .toAttrs()
    ) {
        Column(Modifier.fillMaxWidth()) { content() }
    }
}
